import React, { useState } from 'react';
import { ArrowLeft, Snowflake, Thermometer, Bandage } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';

const FrostbiteAssessment = ({ onNext, onBack, onDataChange, data }) => {
  const [currentScreen, setCurrentScreen] = useState(1);
  const [frostbiteData, setFrostbiteData] = useState({
    initialChecklist: data?.initialChecklist || {},
    rewarmingChecklist: data?.rewarmingChecklist || {},
    postCareChecklist: data?.postCareChecklist || {},
    severity: data?.severity || '',
    notes: data?.notes || ''
  });

  const handleChecklistChange = (section, item, checked) => {
    const newData = {
      ...frostbiteData,
      [section]: {
        ...frostbiteData[section],
        [item]: checked
      }
    };
    setFrostbiteData(newData);
    if (onDataChange) {
      onDataChange({ frostbite_assessment: newData });
    }
  };

  const handleFieldChange = (field, value) => {
    const newData = { ...frostbiteData, [field]: value };
    setFrostbiteData(newData);
    if (onDataChange) {
      onDataChange({ frostbite_assessment: newData });
    }
  };

  const handleComplete = () => {
    if (onDataChange) {
      onDataChange({ frostbite_assessment: frostbiteData });
    }
    onNext();
  };

  const initialChecklistItems = [
    "Estimate exposure time and ambient temperature/wind (wind chill).",
    "Identify affected parts (digits, hands/feet, ears, nose, cheeks).",
    "Assess color/texture (pale, mottled, waxy, firm/wooden), sensation, and capillary refill.",
    "Document numbness or loss of function.",
    "Screen for concurrent hypothermia.",
    "Avoid rubbing/massage and avoid dry heat or open flames."
  ];

  const rewarmingChecklistItems = [
    "Confirm no risk of re-freezing at destination.",
    "Prepare clean circulating warm-water bath (37–39 °C).",
    "Provide analgesia (NSAIDs/opioids) before immersion.",
    "Monitor for pain; stop when tissue becomes pliable.",
    "Observe for color change, edema, and blister formation."
  ];

  const postCareChecklistItems = [
    "Apply sterile, non-adherent dressings; separate digits with dry gauze.",
    "Elevate affected limb(s); avoid weight-bearing if feasible.",
    "Tetanus prophylaxis as indicated.",
    "Start ibuprofen (anti-thromboxane effect) if not contraindicated.",
    "Consider topical aloe vera every 6 hours.",
    "Consider vasodilators (e.g., iloprost where available) or thrombolytics per local protocol.",
    "Assess for compartment syndrome; measure pressures if concern."
  ];

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-black py-4">
      <div className="w-full max-w-sm mx-auto px-4">
        <div className="flex flex-col min-h-screen bg-zinc-900 rounded-3xl border-2 border-cyan-500/30 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b-2 border-cyan-500/30">
            <Button
              onClick={() => currentScreen === 1 ? onBack() : setCurrentScreen(currentScreen - 1)}
              variant="ghost"
              className="flex items-center gap-2 text-cyan-400 text-lg font-bold"
            >
              <ArrowLeft className="w-6 h-6" />
              Back
            </Button>
            <h1 className="text-lg font-black text-cyan-400">
              Frostbite {currentScreen}/4
            </h1>
            <div className="w-16" />
          </div>

          {/* Content */}
          <main className="flex-1 overflow-y-auto p-4 bg-black space-y-4">
            {currentScreen === 1 && (
              <>
                {/* Initial Assessment */}
                <div className="rounded-2xl bg-cyan-900/30 border-2 border-cyan-500/50 p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Snowflake className="w-8 h-8 text-cyan-400" />
                    <h2 className="text-xl font-black text-cyan-400">Initial Assessment</h2>
                  </div>
                  <p className="text-base text-cyan-100 leading-relaxed">
                    Cold injury may coexist with hypothermia. Assess exposure duration, ambient temperature/wind, moisture, and affected areas before rewarming.
                  </p>
                </div>

                {/* Initial Checklist */}
                <div className="space-y-3">
                  <h3 className="text-lg font-bold text-cyan-400">Assessment Checklist</h3>
                  {initialChecklistItems.map((item, idx) => (
                    <label key={idx} className="flex items-start gap-3 p-3 rounded-xl bg-zinc-900 border border-cyan-500/30 cursor-pointer hover:bg-cyan-900/20 transition-colors">
                      <input
                        type="checkbox"
                        checked={frostbiteData.initialChecklist[`item_${idx}`] || false}
                        onChange={(e) => handleChecklistChange('initialChecklist', `item_${idx}`, e.target.checked)}
                        className="mt-1 w-5 h-5 rounded border-cyan-500/50 text-cyan-500 focus:ring-cyan-500"
                      />
                      <span className="text-base text-cyan-100 leading-relaxed">{item}</span>
                    </label>
                  ))}
                </div>
              </>
            )}

            {currentScreen === 2 && (
              <>
                {/* Rewarming */}
                <div className="rounded-2xl bg-cyan-900/30 border-2 border-cyan-500/50 p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Thermometer className="w-8 h-8 text-cyan-400" />
                    <h2 className="text-xl font-black text-cyan-400">Rapid Rewarming</h2>
                  </div>
                  <p className="text-base text-cyan-100 leading-relaxed">
                    Use a circulating water bath at 37–39 °C (98.6–102.2 °F) until tissues are soft and pliable (≈15–30 min). Do not start if there is a risk of re-freezing.
                  </p>
                </div>

                {/* Rewarming Checklist */}
                <div className="space-y-3">
                  <h3 className="text-lg font-bold text-cyan-400">Rewarming Checklist</h3>
                  {rewarmingChecklistItems.map((item, idx) => (
                    <label key={idx} className="flex items-start gap-3 p-3 rounded-xl bg-zinc-900 border border-cyan-500/30 cursor-pointer hover:bg-cyan-900/20 transition-colors">
                      <input
                        type="checkbox"
                        checked={frostbiteData.rewarmingChecklist[`item_${idx}`] || false}
                        onChange={(e) => handleChecklistChange('rewarmingChecklist', `item_${idx}`, e.target.checked)}
                        className="mt-1 w-5 h-5 rounded border-cyan-500/50 text-cyan-500 focus:ring-cyan-500"
                      />
                      <span className="text-base text-cyan-100 leading-relaxed">{item}</span>
                    </label>
                  ))}
                </div>
              </>
            )}

            {currentScreen === 3 && (
              <>
                {/* Post-Rewarming Care */}
                <div className="rounded-2xl bg-cyan-900/30 border-2 border-cyan-500/50 p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Bandage className="w-8 h-8 text-cyan-400" />
                    <h2 className="text-xl font-black text-cyan-400">Protect and Medicate</h2>
                  </div>
                  <p className="text-base text-cyan-100 leading-relaxed">
                    After rewarming, protect tissues, avoid pressure, elevate, and evaluate for reperfusion injury or compartment syndrome.
                  </p>
                </div>

                {/* Post-Care Checklist */}
                <div className="space-y-3">
                  <h3 className="text-lg font-bold text-cyan-400">Post-Rewarming Checklist</h3>
                  {postCareChecklistItems.map((item, idx) => (
                    <label key={idx} className="flex items-start gap-3 p-3 rounded-xl bg-zinc-900 border border-cyan-500/30 cursor-pointer hover:bg-cyan-900/20 transition-colors">
                      <input
                        type="checkbox"
                        checked={frostbiteData.postCareChecklist[`item_${idx}`] || false}
                        onChange={(e) => handleChecklistChange('postCareChecklist', `item_${idx}`, e.target.checked)}
                        className="mt-1 w-5 h-5 rounded border-cyan-500/50 text-cyan-500 focus:ring-cyan-500"
                      />
                      <span className="text-base text-cyan-100 leading-relaxed">{item}</span>
                    </label>
                  ))}
                </div>
              </>
            )}

            {currentScreen === 4 && (
              <>
                {/* Classification */}
                <div className="space-y-4">
                  <h2 className="text-xl font-black text-cyan-400">Frostbite Classification</h2>
                  
                  <div>
                    <Label htmlFor="severity" className="block text-base font-bold text-cyan-400 mb-2">
                      Select Frostbite Degree
                    </Label>
                    <select
                      id="severity"
                      value={frostbiteData.severity}
                      onChange={(e) => handleFieldChange('severity', e.target.value)}
                      className="w-full rounded-xl border-2 border-cyan-500/30 bg-zinc-900 p-4 text-base text-cyan-100 focus:border-cyan-500 focus:ring-cyan-500"
                    >
                      <option value="">Select Degree</option>
                      <option value="first">First-degree (superficial: numbness/erythema, no blisters)</option>
                      <option value="second">Second-degree (clear blisters, edema)</option>
                      <option value="third">Third-degree (hemorrhagic blisters, dermal necrosis)</option>
                      <option value="fourth">Fourth-degree (full-thickness necrosis, possible bone involvement)</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="notes" className="block text-base font-bold text-cyan-400 mb-2">
                      Additional Notes
                    </Label>
                    <textarea
                      id="notes"
                      rows="4"
                      placeholder="Affected area, duration, ambient temperature/wind, rewarming duration, meds given, perfusion status, planned follow-up."
                      value={frostbiteData.notes}
                      onChange={(e) => handleFieldChange('notes', e.target.value)}
                      className="w-full rounded-xl border-2 border-cyan-500/30 bg-zinc-900 p-4 text-base text-cyan-100 placeholder-zinc-500 focus:border-cyan-500 focus:ring-cyan-500"
                    />
                  </div>
                </div>
              </>
            )}
          </main>

          {/* Footer */}
          <div className="p-4 border-t-2 border-cyan-500/30">
            <Button
              onClick={() => currentScreen === 4 ? handleComplete() : setCurrentScreen(currentScreen + 1)}
              className="w-full bg-cyan-500 text-white font-bold py-4 px-6 rounded-xl hover:bg-cyan-600 transition-colors text-lg"
            >
              {currentScreen === 4 ? 'Complete' : 'Continue'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FrostbiteAssessment;
